import React, { useContext, useState, useEffect } from 'react';
import { Button, Typography, Container, Box } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthService from '../../Services/auth.service';
import UserService from '../../Services/user.service';
import './Unauthorized.css';
import logoImage from '../../assests/images/login/logo-blue.svg';
import { NavHistoryContext } from '../../Contexts/NavHistoryContext';

const buttonSx = {
    px: 4,
    py: 1.5,
    fontSize: '1rem',
    borderRadius: '30px',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        transform: 'scale(1.05)',
        boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.4)',
    },
};

function Unauthorized() {
    const navigate = useNavigate();
    const { i18n, t } = useTranslation();
    const navContext = useContext(NavHistoryContext);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            const token = await UserService.getCurrentToken();
            const user = await UserService.getCurrentUser();
            setIsLoggedIn(!!(token && user));
        };
        checkAuth();
    }, []);

    const handleGoBack = () => {
        if (navContext?.goBack) {
            navContext.goBack();
        } else if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate(isLoggedIn ? '/Main' : '/login', { replace: true });
        }
    };

    const handleHome = () => {
        navigate('/Main', { replace: true });
    };

    const handleLogin = () => {
        if (isLoggedIn) {
            AuthService.logout();
        }
        navigate('/login', { replace: true });
    };

    return (
        <Box
            sx={{
                height: '100vh',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <div className="unauthorized-background" />

            <img
                src={logoImage}
                alt="Logo"
                className={`${i18n.language === 'en' ? 'logo-ltr' : 'logo-rtl'}`}
            />

            <Container
                maxWidth="sm"
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    color: 'white',
                    textAlign: 'center',
                    pt: 15,
                }}
            >
                <LockOutlined sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
                <Typography variant="h4" gutterBottom>
                    {t("AccessDenied")}
                </Typography>
                <Typography variant="h5" gutterBottom>
                    {t("NoPermissions")}
                </Typography>
                <Box
                    mt={4}
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: 2,
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGoBack}
                        sx={buttonSx}
                    >
                        {t("GoBack")}
                    </Button>
                    {isLoggedIn ? (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleHome}
                            sx={{
                                ...buttonSx,
                                color: '#F9AA2A',
                                borderWidth: '2px',
                                borderColor: '#F9AA2A',
                                background: 'white',
                                '&:hover': {
                                    ...buttonSx['&:hover'],
                                    background: 'white',
                                },
                            }}
                        >
                            {t("Home")}
                        </Button>
                    ) : (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleLogin}
                            sx={{
                                ...buttonSx,
                                color: '#F9AA2A',
                                borderWidth: '2px',
                                borderColor: '#F9AA2A',
                                background: 'white',
                                '&:hover': {
                                    ...buttonSx['&:hover'],
                                    background: 'white',
                                },
                            }}
                        >
                            {t("Login")}
                        </Button>
                    )}
                </Box>
            </Container>
        </Box>
    );
}

export default Unauthorized;
